/* AudioLearn 2026 — predictive-search.js
   Search overlay (I-004, AH-004): Cmd/Ctrl+K, Escape to close, focus management,
   Shopify Predictive Search API with debounce, popular searches fallback. */

(function () {
  'use strict';

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function buildFocusedSearchQuery(value) {
    var query = value.trim();
    if (!query || /\b(title|tag|vendor|product_type):/i.test(query)) return query;

    query = query.replace(/["\\]/g, ' ').replace(/\s+/g, ' ').trim();
    if (!query) return '';
    /* ASE titles are inconsistent across the catalogue, while its
       certification tag is reliable. Keep the short abbreviation useful. */
    if (/^ase(?:\s|$)/i.test(query)) {
      var aseTopic = query.replace(/^ase\s*/i, '').trim();
      return aseTopic
        ? 'tag:"ASE Certification" AND title:"' + aseTopic + '"'
        : '(title:ASE OR tag:ASE OR tag:"ASE Certification")';
    }
    return 'title:"' + query + '"';
  }

  function trapFocus(container, event) {
    var nodes = Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (element) {
      return element.offsetParent !== null;
    });
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  window.customElements.define('al-search-overlay', class extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form[role="search"]');
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-search-results]');
      this.popular = this.querySelector('[data-popular-searches]');
      this.status = this.querySelector('[data-search-status]');
      this.trigger = null;
      this.triggers = Array.prototype.slice.call(document.querySelectorAll('[data-search-open]'));
      this.timer = null;
      this.requestController = null;

      this.triggers.forEach(function (btn) {
        btn.setAttribute('aria-controls', this.id);
        btn.setAttribute('aria-expanded', 'false');
        btn.addEventListener('click', function () { this.open(btn); }.bind(this));
      }, this);

      document.addEventListener('keydown', function (e) {
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); this.open(); }
        if (e.key === 'Escape' && this.dataset.open === 'true') this.close();
      }.bind(this));

      this.addEventListener('keydown', function (e) {
        if (e.key === 'Tab' && this.dataset.open === 'true') trapFocus(this, e);
      }.bind(this));

      this.addEventListener('click', function (e) { if (e.target === this) this.close(); });
      this.querySelectorAll('[data-search-close]').forEach(function (btn) {
        btn.addEventListener('click', this.close.bind(this));
      }, this);

      if (this.input) {
        this.input.addEventListener('input', function () {
          window.clearTimeout(this.timer);
          this.timer = window.setTimeout(this.fetchSuggestions.bind(this), 250);
        }.bind(this));
      }

      if (this.form && this.input) {
        this.form.addEventListener('submit', function () {
          this.input.value = buildFocusedSearchQuery(this.input.value);
        }.bind(this));
      }
    }

    open(trigger) {
      this.trigger = trigger || document.activeElement;
      this.dataset.open = 'true';
      this.triggers.forEach(function (btn) { btn.setAttribute('aria-expanded', 'true'); });
      document.body.style.overflow = 'hidden';
      var self = this;
      window.setTimeout(function () { if (self.input) self.input.focus(); }, 50);
    }

    close() {
      window.clearTimeout(this.timer);
      if (this.requestController) {
        this.requestController.abort();
        this.requestController = null;
      }
      this.dataset.open = 'false';
      this.triggers.forEach(function (btn) { btn.setAttribute('aria-expanded', 'false'); });
      document.body.style.overflow = '';
      if (this.trigger && this.trigger.focus) this.trigger.focus();
    }

    fetchSuggestions() {
      var q = this.input.value.trim();
      if (this.requestController) {
        this.requestController.abort();
        this.requestController = null;
      }
      if (!q) {
        this.results.innerHTML = '';
        if (this.popular) this.popular.hidden = false;
        if (this.status) this.status.textContent = '';
        return;
      }
      if (this.popular) this.popular.hidden = true;
      this.results.innerHTML = '';
      if (this.status) this.status.textContent = 'Searching';

      var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      var focusedQuery = buildFocusedSearchQuery(q);
      var url = root + 'search/suggest?q=' + encodeURIComponent(focusedQuery) +
        '&resources[type]=product&resources[limit]=6&resources[options][fields]=title&resources[options][unavailable_products]=hide&section_id=predictive-search';
      var requestedQuery = q;
      this.requestController = new AbortController();

      fetch(url, { signal: this.requestController.signal })
        .then(function (r) {
          if (!r.ok) throw new Error('Predictive search request failed');
          return r.text();
        })
        .then(function (html) {
          if (this.input.value.trim() !== requestedQuery) return;
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var content = doc.querySelector('[data-predictive-search-content]');
          var total = content ? Number(content.dataset.resultCount || 0) : 0;
          this.results.innerHTML = content ? content.outerHTML : '';
          if (this.status) this.status.textContent = total + ' suggestions available';
        }.bind(this))
        .catch(function (error) {
          if (error.name !== 'AbortError' && this.input.value.trim() === requestedQuery) {
            this.results.innerHTML = '';
            if (this.status) this.status.textContent = 'Suggestions unavailable';
          }
        }.bind(this));
    }
  });

  document.querySelectorAll('[data-focused-search-form]').forEach(function (form) {
    var input = form.querySelector('input[type="search"][name="q"]');
    if (!input) return;
    form.addEventListener('submit', function () {
      input.value = buildFocusedSearchQuery(input.value);
    });
  });
})();
